import React, {
    useRef,
    useImperativeHandle,
    forwardRef,
    useEffect,
} from "react";
import type { ComponentType } from "react";
import { Player, PlayerRef } from "@remotion/player";
import { TripVideo, mapFrameCount } from "./remotion/composition";
import type { TripVideoProps } from "./remotion/composition";
import type { TripData, VideoConfig } from "./types";
import {
    INTRO_FRAMES,
    OUTRO_FRAMES,
    VIDEO_WIDTH,
    VIDEO_HEIGHT,
    VIDEO_FPS,
} from "./types";

export interface TripPlayerHandle {
    seekTo: (frame: number) => void;
    play: () => void;
    pause: () => void;
    getTotalFrames: () => number;
    getCurrentFrame: () => number;
}

interface Props {
    tripData: TripData;
    config: VideoConfig;
    mapboxToken: string;
    onFrameChange?: (frame: number) => void;
}

export const TripPlayer = forwardRef<TripPlayerHandle, Props>(
    ({ tripData, config, mapboxToken, onFrameChange }, ref) => {
        const playerRef = useRef<PlayerRef>(null);

        const mapFrames = mapFrameCount(tripData, config);
        const totalFrames = INTRO_FRAMES + mapFrames + OUTRO_FRAMES;

        const compositionProps: TripVideoProps = { tripData, config, mapboxToken };

        useImperativeHandle(ref, () => ({
            seekTo: (frame) => playerRef.current?.seekTo(frame),
            play: () => playerRef.current?.play(),
            pause: () => playerRef.current?.pause(),
            getTotalFrames: () => totalFrames,
            getCurrentFrame: () => playerRef.current?.getCurrentFrame() ?? 0,
        }));

        useEffect(() => {
            const player = playerRef.current;
            if (!player) return;
            const handler = ({ detail }: { detail: { frame: number } }) => {
                if (detail.frame >= totalFrames - 1) {
                    player.pause();
                }
                onFrameChange?.(detail.frame);
            };
            player.addEventListener("frameupdate", handler);
            return () => player.removeEventListener("frameupdate", handler);
        }, [onFrameChange, totalFrames]);

        return (
            <Player
                ref={playerRef}
                component={
                    TripVideo as unknown as ComponentType<
                        Record<string, unknown>
                    >
                }
                inputProps={
                    compositionProps as unknown as Record<string, unknown>
                }
                durationInFrames={totalFrames}
                fps={VIDEO_FPS}
                compositionWidth={VIDEO_WIDTH}
                compositionHeight={VIDEO_HEIGHT}
                style={{ width: "100%", height: "100%" }}
                controls={false}
                loop={false}
                acknowledgeRemotionLicense
            />
        );
    },
);
